import { AngleFrom, NumberInputFrom } from '@tencent/wb-ext-form';
import { IDrawerInputManager, ITempModelMethod } from './drawer-state-manager.interface';
import { SelectedModelType } from '@src/slide/application/service/selection/selection-service.interface';

export class DrawerInputManager implements IDrawerInputManager {
    /**
    * 输入框聚焦可能有两种情况：一种是手动输入数字，一种是点击了加减按钮
    */
    public isFocus: boolean; // 输入框是否聚焦
    /**
    * 记录是否正在手动输入
    * 目前输入框需支持交互：输入框可随意输入数字，但是显示的时候如果超过极限值，则仅显示极限值
    */
    public isInputting: boolean; // 输入框是否聚焦
    /**
    * 是否强制更新，比如输入框内回车触发强制更新，但不失焦
    */
    public isForceUpdate: boolean; // 输入框是否聚焦
    /**
     * 侧边栏当前作用的待更新的model，默认等于currentModel
     * 有一种情况可能不等于currentModel，例如：输入框失焦时切换了元素
     */
    public toUpateModel: SelectedModelType;
    /**
     * 缓存所有的输入框的正在输入的值
     */
    public numberInputValue: Record<string, number> = {};
    /**
    * 变更来源
    * 可能来自输入框、输入框加减按钮、滑动条、角度旋转盘
    * 当使用slider/angle组件时，事件可能来自于滑块/角度旋转盘
    */
    public changeFrom?: NumberInputFrom | AngleFrom;
    /**
     * 是否仅改变UI
     */
    public isOnlyChangeUI: boolean;
    /**
     * 当前选中的model
     */
    private currentModel: SelectedModelType;
    /**
     * 操作输入框时临时缓存一个model，用于对比失焦时元素是否切换
     */
    private inputTempModel: SelectedModelType;
    /**
     * 正在输入的feature的key值
     */
    private inputtingFeatureKey?: string;
    /**
     * 记录feature的initProps回调。
     * 主要适用于带滑块的输入框，因为拖拽滑块时为避免抖动，所以需要临时缓存下数据
     */
    private initPropsCallbacks: Record<string, () => void> = {};
    /**
     * 缓存此时正在变更调用的接口，方便手动触发
     */
    private tempMethod: ITempModelMethod | undefined;

    public updateCurrentModel = (model: SelectedModelType) => {
        this.currentModel = model;
        this.toUpateModel = model;
    };

    public setInputFocus = (isFocus: boolean, model?: SelectedModelType) => {
        this.isFocus = isFocus;
        this.isInputting = isFocus;
        // 聚焦时更新缓存值
        if (isFocus) {
            this.inputTempModel = model ?? this.currentModel;
        } else {
            this.clearInputValueCache();
        }
    };

    /**
    * 在失焦触发onChange事件前，初始化状态
    * @param key 用来标识对应的feature
    * @returns false 表示未失焦；true表示已失焦
    */
    public initBeforeChangeOnBlur = (key: string): boolean => {
        this.inputtingFeatureKey = key;
        if (!this.isChangeOnBlur()) return false;
        // 表明失焦触发的变更还未来的及作用到目标model上，就切换了元素
        if (this.toUpateModel !== this.inputTempModel) {
            this.toUpateModel = this.inputTempModel;
            // model更新后，相应的初始化数据
            this.executeInitPropsCallback(key);
        }
        return true;
    };

    /**
     * 在失焦触发onChange事件后，重置状态
     * @param key 用来标识对应的feature
     * @returns false 表示未失焦；true表示已失焦
     */
    public resetAfterChangeOnBlur = (key: string): boolean => {
        if (!this.isChangeOnBlur()) return false;
        this.changeFrom = undefined;
        this.inputTempModel = null;
        // 失焦后重置toUpateModel的值依旧为当前选中的model
        this.toUpateModel = this.currentModel;
        // model更新后，相应的更新数据
        this.executeInitPropsCallback(key);
        // 重置key
        this.inputtingFeatureKey = undefined;
        // 重置缓存的方法
        this.tempMethod = undefined;
        return true;
    };

    /**
     * 清除侧边栏输入框输入时的缓存值
     */
    public clearInputValueCache = () => {
        this.isInputting = false;
        Object.keys(this.numberInputValue).forEach((key) => {
            delete this.numberInputValue[key];
        });
    };

    public getCachedInputValue = (key: string, propName: string): number | undefined => {
        if (key === this.inputtingFeatureKey && this.isInputting) {
            return this.numberInputValue[propName];
        }
    };

    /**
     * 侧边栏打开时，初始化侧边栏数据
     * @param key 用来标识对应的feature
     */
    public executeInitPropsCallback = (key?: string) => {
        if (key && this.initPropsCallbacks[key]) {
            this.initPropsCallbacks[key]();
        } else {
            Object.keys(this.initPropsCallbacks).forEach((key) => {
                this.initPropsCallbacks[key]();
            });
        }
    };

    /**
     * 添加初始化方法回调
     * @param key 用来标识对应的feature
     */
    public addInitPropsCallback = (key: string, callback: () => void) => {
        if (!this.initPropsCallbacks[key]) {
            this.initPropsCallbacks[key] = callback;
            callback();
        }
    };

    /**
     * 可能操作完加减按钮后，如果未失焦继续输入则需要更新状态
     */
    public updateInputStateReInput = () => {
        if (this.isFocus) {
            this.isInputting = true;
        }
    };

    /**
     * 手动触发输入框对应的更新接口，且数据落盘
     */
    public forceUpdateInput = () => {
        if (!this.tempMethod) return;
        this.isForceUpdate = true;
        const { className, method, args } = this.tempMethod;
        method.apply(className, args);
        this.isForceUpdate = false;
    };

    public updateTempMethod = (tempMethod?: ITempModelMethod) => {
        this.tempMethod = tempMethod;
    };

    /**
     * 是否是失焦触发的onChange事件
     * onChange事件是在blur后触发，此时isFocus为false，需要借助changeFrom判断此次事件是否来自输入框
     */
    private isChangeOnBlur = () => {
        const isInputChange = (this.changeFrom === NumberInputFrom.INPUT
            || this.changeFrom === NumberInputFrom.ADD_BTN
            || this.changeFrom === NumberInputFrom.REDUCE_BTN);
        return isInputChange && !this.isFocus;
    };
}
